import React, { useEffect, useRef, useState } from 'react';

const verbiage = {
  en: {
    eyebrow: 'Verify it’s you',
    title: 'Enter your code',
    body: 'We sent a 5-digit code. Enter it below to continue.',
    resend: 'Send a new code',
    invalid: 'That code didn’t match. Try again or request a new one.',
    expiresIn: 'Expires in',
    expired: 'Code expired',
    codeLabel: 'Verification code',
  },
  es: {
    eyebrow: 'Verifique su identidad',
    title: 'Ingresar el código',
    body: 'Le enviamos un código de 5 dígitos. Ingréselo abajo para continuar.',
    resend: 'Enviar un código nuevo',
    invalid: 'El código no es correcto. Intente otra vez o solicite uno nuevo.',
    expiresIn: 'Expira en',
    expired: 'Código vencido',
    codeLabel: 'Código de verificación',
  },
};

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const r = s % 60;

  return `${m}:${String(r).padStart(2, '0')}`;
};

type Props = {
  authResponse: string;
  onSubmitCode: (code: number) => void | Promise<void>;
  onHandleNewCode: () => void;
  loading?: boolean;
  language?: 'en' | 'es';
  invalidCode?: boolean;
  timeLeft?: number;
  primaryColor?: string;
};

const CODE_LENGTH = 5;

const RequestCodeInput: React.FC<Props> = ({
  authResponse,
  onSubmitCode,
  onHandleNewCode,
  loading,
  language = 'en',
  invalidCode,
  timeLeft = 0,
  primaryColor = '#77bd43',
}) => {
  const v = verbiage[language === 'es' ? 'es' : 'en'];
  const [code, setCode] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-submit once the user fills (or autofills) the full code.
  useEffect(() => {
    if (code.length === CODE_LENGTH && !loading) {
      onSubmitCode(Number(code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Reset on invalidCode flip so the user can re-enter.
  useEffect(() => {
    if (invalidCode) {
      setCode('');
      inputRef.current?.focus();
    }
  }, [invalidCode]);

  const handleChange = (raw: string) => {
    const cleaned = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);

    setCode(cleaned);
  };

  return (
    <main className="min-h-dvh flex flex-col items-center justify-center antialiased bg-gray-50 dark:bg-[var(--color-bg-page)] px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="w-full max-w-md">
        <div className="rounded-3xl overflow-hidden ring-1 ring-gray-950/5 dark:ring-white/10 bg-white dark:bg-white/5">
          <div className="p-6 sm:p-8 text-white" style={{ backgroundColor: primaryColor }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/75">
              {v.eyebrow}
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">{v.title}</h1>
            <p className="mt-3 text-sm text-white/90 max-w-prose">{authResponse || v.body}</p>
          </div>

          <div className="p-5 sm:p-6 flex flex-col gap-5">
            {/* Single rounded readout in the kiosk phone-display
                style: each digit position renders as a fixed `1ch`
                slot showing either the typed digit or a faint
                bullet. A full-width hidden input behind the readout
                catches every keystroke (with
                `autocomplete="one-time-code"` so iOS can fill the
                SMS code in one tap). The caret is hidden so the
                display stays clean. */}
            <label
              className="relative block cursor-text rounded-2xl bg-gray-50 dark:bg-white/5 ring-2 ring-gray-950/10 dark:ring-white/15 px-5 py-5 sm:py-6 focus-within:ring-[--ring] focus-within:bg-white dark:focus-within:bg-white/10 transition-shadow"
              style={{ ['--ring' as any]: primaryColor } as React.CSSProperties}
              aria-label={v.codeLabel}
            >
              <div
                className="text-center text-3xl sm:text-4xl font-bold tabular-nums select-none"
                aria-hidden="true"
              >
                {Array.from({ length: CODE_LENGTH }).map((_, i) => {
                  const ch = code[i];

                  return (
                    <span
                      key={i}
                      className={`inline-block w-[1ch] text-center ${
                        ch ? 'text-gray-900 dark:text-gray-100' : 'text-gray-300 dark:text-gray-600'
                      } ${i > 0 ? 'ml-3 sm:ml-4' : ''}`}
                    >
                      {ch ?? '•'}
                    </span>
                  );
                })}
              </div>
              <input
                ref={inputRef}
                type="text"
                name="code"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                aria-label={v.codeLabel}
                maxLength={CODE_LENGTH}
                value={code}
                disabled={loading}
                onChange={e => handleChange(e.target.value)}
                className="absolute inset-0 size-full opacity-0 cursor-text [caret-color:transparent]"
              />
            </label>

            {invalidCode && <p className="text-sm text-red-600 dark:text-red-400">{v.invalid}</p>}

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onHandleNewCode}
                disabled={loading || timeLeft > 0}
                className="text-sm font-medium hover:underline disabled:opacity-40 disabled:no-underline"
                style={{ color: primaryColor }}
              >
                {v.resend}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {timeLeft > 0
                  ? `${v.expiresIn} ${formatTime(timeLeft)}`
                  : authResponse
                    ? v.expired
                    : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RequestCodeInput;
